import { useMutation, useQueryClient } from '@tanstack/react-query'

import {
  applicationsAccessCreateMutation,
  applicationsApplicationsAccessListOptions,
  applicationsApplicationsAccessListQueryKey,
  organizationsAccessListOptions,
  organizationsAccessRolesCreateMutation,
  organizationsRolesListOptions,
  type ApplicationAccessList
} from 'src/api/main'
import { mergeManagedRoleIds } from 'src/features/users/utils'
import { useInvalidatePredicateQueries } from 'src/hooks'

import { buildAppAccessApplications } from '../utils/appAccess'

const PER_PAGE_MAX = 200

/**
 * Removes a member's access to one app. Resolves the member's
 * OrganizationAccess record, their full app list, and this app's roles at
 * call time, then re-posts the declarative app list without this app and
 * drops the app's roles from the member's role set (other roles preserved).
 */
export const useAppAccessRemoval = (
  applicationId: number,
  organizationId: number
) => {
  const queryClient = useQueryClient()
  const { invalidateQueries } = useInvalidatePredicateQueries()

  const { mutateAsync: setAppAccess, isPending: isSettingAccess } = useMutation(
    { ...applicationsAccessCreateMutation() }
  )
  const { mutateAsync: setRoles, isPending: isSettingRoles } = useMutation({
    ...organizationsAccessRolesCreateMutation()
  })

  const removeAccess = async (target: ApplicationAccessList) => {
    const [memberPage, accessPage, rolesPage] = await Promise.all([
      queryClient.fetchQuery(
        organizationsAccessListOptions({
          query: {
            organization: organizationId,
            user: target.user.id,
            per_page: 10
          }
        })
      ),
      queryClient.fetchQuery(
        applicationsApplicationsAccessListOptions({
          query: {
            organization: organizationId,
            user: target.user.id,
            per_page: PER_PAGE_MAX
          }
        })
      ),
      queryClient.fetchQuery(
        organizationsRolesListOptions({
          query: {
            application: applicationId,
            organization: organizationId,
            per_page: PER_PAGE_MAX
          }
        })
      )
    ])

    const member = memberPage.results?.[0]
    if (!member) {
      throw new Error('Workspace access record not found for this user')
    }

    await setAppAccess({
      path: { organization_id: organizationId },
      body: {
        users: [member.id],
        applications: buildAppAccessApplications(
          accessPage.results ?? [],
          applicationId,
          null
        )
      }
    })

    const managed = new Set((rolesPage.results ?? []).map((r) => r.id))
    await setRoles({
      path: { id: member.id },
      body: {
        roles: mergeManagedRoleIds(
          member.roles.map((r) => r.id),
          managed,
          []
        )
      }
    })

    await invalidateQueries([applicationsApplicationsAccessListQueryKey()])
  }

  return { removeAccess, isRemoving: isSettingAccess || isSettingRoles }
}
